import { motion } from 'framer-motion';
import { Camera, Gauge, Hash, MapPin, Save, Upload } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import loggingService from '../../services/loggingService';
import { typography } from '../../styles/typography';
import { Button, Card } from '../ui';
import Input from '../ui/input.js';
import {
  createVehicle,
  getSites,
  getVehicleTypes,
  validateVehicleData,
} from './vehicleOnboardingApi.js';

import './VehicleOnboarding.css';

// Constants
const VEHICLE_COLORS = [
  'White',
  'Black',
  'Silver',
  'Red',
  'Blue',
  'Green',
  'Yellow',
  'Orange',
  'Gray',
  'Brown',
];

const INITIAL_FORM_STATE = {
  // tenant_id: '1',
  area_available: '',
  model_name: '',
  registration_no: '',
  vehicle_type: '',
  engine_cc: '',
  vehicle_color: '',
  seating_capacity: '',
  cargo_capacity: '',
  base_location: '',
  imageData: null,
};

// Animation variants
const ANIMATION_VARIANTS = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const VehicleOnboarding = () => {
  // Log screen view
  useScreenLogging('VehicleOnboarding', {
    component: 'VehicleOnboarding',
  });

  // Form state
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Data loading state
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [sites, setSites] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(true);

  // Image handling state
  const [imageData, setImageData] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [hasImage, setHasImage] = useState(false);

  // Camera functionality state
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraError, setCameraError] = useState('');
  const [isCapturing, setIsCapturing] = useState(false);
  const [showCameraPreview, setShowCameraPreview] = useState(false);

  // Refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  // Effects
  useEffect(() => {
    fetchVehicleData();
  }, []);

  // Data fetching functions
  const fetchVehicleData = async () => {
    setIsLoadingData(true);
    try {
      console.log('Fetching vehicle types and sites from API...');

      const [typesResponse, sitesResponse] = await Promise.all([
        getVehicleTypes(),
        getSites(),
      ]);

      console.log('Vehicle types response:', typesResponse);
      console.log('Sites response:', sitesResponse);

      setVehicleTypes(typesResponse);
      setSites(sitesResponse);
      
      // Log API event for fetching vehicle data
      try {
        await loggingService.logInfo(
          'vehicle_onboarding_fetch_data',
          'Fetched vehicle types and sites',
          {
            endpoint: '/vehicle/types and /site/getall',
            method: 'GET',
            vehicleTypesCount: Array.isArray(typesResponse) ? typesResponse.length : 0,
            sitesCount: Array.isArray(sitesResponse) ? sitesResponse.length : 0,
          }
        );
      } catch (logError) {
        console.warn('[VehicleOnboarding] Failed to log event:', logError);
      }
    } catch (error) {
      console.error('Failed to fetch vehicle data:', error);
      setError(
        'Failed to load vehicle types or sites. Please refresh the page.'
      );
    } finally {
      setIsLoadingData(false);
    }
  };

  // Event handlers
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleImageUpload = event => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = e => {
      setImageData(e.target.result);
      setImagePreview(e.target.result);
      setHasImage(true);
      setError('');
    };
    reader.readAsDataURL(file);
  };

  // Camera functions
  const startCamera = async () => {
    try {
      setCameraError('');
      setIsCameraActive(true);
      setShowCameraPreview(true);

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
      });

      setCameraStream(stream);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      setCameraError('Unable to access camera. Please check permissions.');
      setIsCameraActive(false);
      setShowCameraPreview(false);
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setIsCameraActive(false);
    setShowCameraPreview(false);
    setCameraError('');
  };

  const captureImage = () => {
    if (!videoRef.current || !canvasRef.current) return;

    setIsCapturing(true);

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0);

    const imageDataUrl = canvas.toDataURL('image/jpeg', 0.8);
    setImageData(imageDataUrl);
    setImagePreview(imageDataUrl);
    setHasImage(true);
    setError('');
    setIsCapturing(false);

    stopCamera();
  };

  // Form submission
  const handleSubmit = async e => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validate form data
      const validation = validateVehicleData(formData);
      if (!validation.isValid) {
        setError(`Validation failed: ${validation.errors.join(', ')}`);
        return;
      }

      // Prepare payload matching Flutter implementation
      const payload = {
        area_available: formData.area_available,
        model_name: formData.model_name,
        registration_no: formData.registration_no,
        vehicle_type: formData.vehicle_type,
        engine_cc: formData.engine_cc,
        vehicle_color: formData.vehicle_color,
        seating_capacity: formData.seating_capacity,
        cargo_capacity: formData.cargo_capacity,
        base_location: formData.base_location,
        imageData: imageData,
      };

      console.log('Submitting vehicle data:', payload);

      // Create vehicle
      const result = await createVehicle(payload);
      
      // Log API event for creating vehicle
      try {
        await loggingService.logInfo(
          'vehicle_onboarding_create',
          'Created vehicle',
          {
            endpoint: '/vehicle/create',
            method: 'POST',
            registrationNo: payload.registration_no,
            vehicleType: payload.vehicle_type,
            hasImage: !!payload.imageData,
          }
        );
      } catch (logError) {
        console.warn('[VehicleOnboarding] Failed to log event:', logError);
      }
      
      setSuccess('Vehicle onboarded successfully!');
      console.log('Vehicle created:', result);

      // Reset form
      resetForm();
    } catch (error) {
      console.error('Error onboarding vehicle:', error);
      setError(error.message || 'Failed to onboard vehicle. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Utility functions
  const resetForm = () => {
    setFormData(INITIAL_FORM_STATE);
    setImageData(null);
    setImagePreview(null);
    setHasImage(false);
  };

  const clearForm = () => {
    resetForm();
    setError('');
    setSuccess('');
  };

  const removeImage = () => {
    setImagePreview(null);
    setImageData(null);
    setHasImage(false);
  };

  // Loading state
  if (isLoadingData) {
    return (
      <div className="w-full h-full bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
        <div className="max-w-6xl mx-auto h-full flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading vehicle onboarding data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Render functions
  const renderHeader = () => (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-center mb-8 bg-transparent pt-0 pb-0"

    >
      <h1
        className={typography.h1}
        style={{ color: '#000000', marginBottom: '0.5rem' }}
      >
        Vehicle Onboarding
      </h1>
      <p className={typography.p} style={{ color: '#6B7280' }}>
        Complete the form below to onboard a new vehicle
      </p>
    </motion.div>
  );

  const renderImageSection = () => (
    <Card.Glassmorphism className="p-6">
      <div className="text-center">
        <h2
          className={typography.h2}
          style={{ color: '#000000', marginBottom: '1.5rem' }}
        >
          Vehicle Image
        </h2>

        <div className="flex flex-col items-center gap-4">
          {/* Image Preview */}
          <div className="relative">
            {imagePreview ? (
              <div className="w-32 h-32 rounded-lg border-4 border-gray-300 overflow-hidden">
                <img
                  src={imagePreview}
                  alt="Vehicle preview"
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={removeImage}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-colors"
                >
                  ×
                </button>
              </div>
            ) : (
              <div className="w-32 h-32 rounded-lg border-4 border-dashed border-gray-300 flex items-center justify-center bg-gray-50">
                <Camera className="w-12 h-12 text-gray-400" />
              </div>
            )}
          </div>

          {/* Upload Buttons */}
          <div className="flex gap-4">
            <Button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
              style={{ color: '#ffffff' }}
            >
              <Upload className="w-4 h-4" style={{ color: '#ffffff' }} />
              Upload Image
            </Button>
            <Button
              type="button"
              onClick={startCamera}
              className={`flex items-center gap-2 ${
                isCameraActive
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-green-600 hover:bg-green-700'
              }`}
              style={{ color: '#ffffff' }}
            >
              <Camera className="w-4 h-4" style={{ color: '#ffffff' }} />
              {isCameraActive ? 'Stop Camera' : 'Take Photo'}
            </Button>
          </div>

          {/* Camera Preview */}
          {showCameraPreview && (
            <div className="mt-4 p-4 border border-gray-300 rounded-lg bg-gray-50">
              <div className="text-center mb-3">
                <h3 className="text-lg font-medium text-gray-800 mb-2">
                  Camera Preview
                </h3>
                {cameraError && (
                  <p className="text-red-600 text-sm mb-2">{cameraError}</p>
                )}
              </div>

              <div className="flex flex-col items-center gap-4">
                <div className="relative">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-80 h-60 rounded-lg border border-gray-300 bg-black"
                  />

                  <div className="flex justify-center gap-3 mt-3">
                    <Button
                      type="button"
                      onClick={captureImage}
                      disabled={isCapturing}
                      className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 px-4 py-2"
                      style={{ color: '#ffffff' }}
                    >
                      {isCapturing ? 'Capturing...' : 'Capture Photo'}
                    </Button>

                    <Button
                      type="button"
                      onClick={stopCamera}
                      className="bg-gray-600 hover:bg-gray-700 px-4 py-2"
                      style={{ color: '#ffffff' }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Hidden Elements */}
          <canvas ref={canvasRef} className="hidden" width="640" height="480" />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
          />
        </div>
      </div>
    </Card.Glassmorphism>
  );

  const renderVehicleIdentification = () => (
    <Card.Gradient className="p-6">
      <div className="flex items-center gap-3 mb-6">
        <Hash className="w-6 h-6 text-blue-600" />
        <h2 className={typography.h2} style={{ color: '#000000' }}>
          Vehicle Identification
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label
            htmlFor="registration_no"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Registration Number *
          </label>
          <Input
            id="registration_no"
            type="text"
            value={formData.registration_no}
            onChange={e =>
              handleInputChange('registration_no', e.target.value.toUpperCase())
            }
            placeholder="e.g., KA01AB1234"
            required
          />
        </div>

        <div>
          <label
            htmlFor="vehicle_type"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Vehicle Type *
          </label>
          <select
            id="vehicle_type"
            value={formData.vehicle_type}
            onChange={e => handleInputChange('vehicle_type', e.target.value)}
            required
            className="w-full p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="" disabled>
              Select Vehicle Type
            </option>
            {vehicleTypes.map(type => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="model_name"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Model Name *
          </label>
          <Input
            id="model_name"
            type="text"
            value={formData.model_name}
            onChange={e => handleInputChange('model_name', e.target.value)}
            placeholder="e.g., Camry, Civic, F-150"
            required
          />
        </div>
      </div>
    </Card.Gradient>
  );

  const renderVehicleSpecifications = () => (
    <Card.Light className="p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
          <Gauge className="w-5 h-5 text-white" />
        </div>
        <h2 className={typography.h2} style={{ color: '#000000' }}>
          Vehicle Specifications
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label
            htmlFor="engine_cc"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Engine CC
          </label>
          <Input
            id="engine_cc"
            type="text"
            value={formData.engine_cc}
            onChange={e => {
              // Only allow numbers
              const value = e.target.value.replace(/[^0-9]/g, '');
              handleInputChange('engine_cc', value);
            }}
            placeholder="e.g., 800"
          />
        </div>

        <div>
          <label
            htmlFor="vehicle_color"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Color
          </label>
          <select
            id="vehicle_color"
            value={formData.vehicle_color}
            onChange={e => handleInputChange('vehicle_color', e.target.value)}
            className="w-full p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="" disabled>
              Select Color
            </option>
            {VEHICLE_COLORS.map(color => (
              <option key={color} value={color}>
                {color}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="seating_capacity"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Seating Capacity
          </label>
          <Input
            id="seating_capacity"
            type="number"
            value={formData.seating_capacity}
            onChange={e =>
              handleInputChange('seating_capacity', e.target.value)
            }
            placeholder="e.g., 5"
            min="1"
            max="100"
          />
        </div>

        <div>
          <label
            htmlFor="cargo_capacity"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Cargo Capacity (kg)
          </label>
          <Input
            id="cargo_capacity"
            type="text"
            value={formData.cargo_capacity}
            onChange={e => {
              const value = e.target.value.replace(/[^0-9]/g, '');
              handleInputChange('cargo_capacity', value);
            }}
            placeholder="e.g., 500"
          />
        </div>
      </div>
    </Card.Light>
  );

  const renderLocationSection = () => (
    <Card.Light className="p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center">
          <MapPin className="w-5 h-5 text-white" />
        </div>
        <h2 className={typography.h2} style={{ color: '#000000' }}>
          Location & Assignment
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label
            htmlFor="base_location"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Base Location *
          </label>
          <select
            id="base_location"
            value={formData.base_location}
            onChange={e => handleInputChange('base_location', e.target.value)}
            required
            className="w-full p-3 rounded-lg border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="" disabled>
              Select Base Location
            </option>
            {[...sites]
              .sort((a, b) => {
                const nameA = (a.site_name || '').toLowerCase();
                const nameB = (b.site_name || '').toLowerCase();
                return nameA.localeCompare(nameB);
              })
              .map(site => (
                <option key={site.site_id} value={site.site_id}>
                  {site.site_name} - {site.address || site.site_address || ''}
                </option>
              ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="area_available"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Area Available
          </label>
          <Input
            id="area_available"
            type="text"
            value={formData.area_available}
            onChange={e => handleInputChange('area_available', e.target.value)}
            placeholder="e.g., Bangalore, Mumbai"
            className="bg-white border-gray-300 text-gray-900 placeholder-gray-500"
          />
        </div>
      </div>
    </Card.Light>
  );

  const renderMessages = () => (
    <>
      {error && (
        <div className="self-stretch p-3 bg-red-50/40 rounded-xl outline outline-1 outline-offset-[-1px] outline-red-200/0 flex flex-col justify-start items-start gap-2.5">
          <div className="justify-start text-red-700 text-lg font-normal font-['Open_Sans'] leading-relaxed">
            {error}
          </div>
        </div>
      )}

      {success && (
        <div className="self-stretch p-3 bg-green-50/40 rounded-xl outline outline-1 outline-offset-[-1px] outline-green-200/0 flex flex-col justify-start items-start gap-2.5">
          <div className="justify-start text-green-700 text-lg font-normal font-['Open_Sans'] leading-relaxed">
            {success}
          </div>
        </div>
      )}
    </>
  );

  const renderFormActions = () => (
    <div className="flex justify-end gap-4">
      <Button
        type="button"
        onClick={clearForm}
        disabled={isLoading}
        className="bg-gray-600 hover:bg-gray-700 px-6 py-3 rounded-lg font-medium"
        style={{ color: '#ffffff' }}
      >
        Clear Form
      </Button>

      <Button
        type="submit"
        disabled={isLoading}
        className="bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-lg font-medium flex items-center gap-2"
        style={{ color: '#ffffff' }}
      >
        {isLoading ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            Onboarding Vehicle...
          </>
        ) : (
          <>
            <Save className="w-4 h-4" style={{ color: '#ffffff' }} />
            Onboard Vehicle
          </>
        )}
      </Button>
    </div>
  );

  return (
    <div className="w-full h-auto bg-transparent p-0 m-0">
    <div className="max-w-6xl mx-auto h-auto">
  
        {renderHeader()}

        <form onSubmit={handleSubmit} className="h-full space-y-8">
          {renderImageSection()}
          {renderVehicleIdentification()}
          {renderVehicleSpecifications()}
          {renderLocationSection()}
          {renderMessages()}
          {renderFormActions()}
        </form>
      </div>
    </div>
  );
};

export default VehicleOnboarding;
